/**
 * FieldOverride: pure domain — type, merge, and JSON serialization.
 * No I/O. Storage lives in adapters/storage/field-override-adapter.
 */

import type { Cell, FieldConfig } from "./types.js";

export interface FieldOverride {
  /** @deprecated Phase D: use entryCells/exitCell/walkableCells instead. */
  waypoints?: Cell[];
  blockedCells?: Cell[];
  wavesNum?: number;
  monstersPerWave?: number;
  monsterBaseHp?: number;
}

/** Apply override on top of the base config (shallow merge of specified fields). */
export function applyOverride(base: FieldConfig, ov: FieldOverride): FieldConfig {
  return {
    ...base,
    // Legacy waypoints override — still supported for editor-saved overrides.
    // If waypoints provided, derive entry/exit from them (single-entry compat).
    ...(ov.waypoints && ov.waypoints.length >= 2 ? {
      waypoints: ov.waypoints,
      entryCells: [{ ...ov.waypoints[0] }],
      exitCell: { ...ov.waypoints[ov.waypoints.length - 1] },
      // walkableCells will be recomputed in createSession from waypoints fallback.
      walkableCells: undefined,
    } : {}),
    blockedCells: ov.blockedCells ?? base.blockedCells,
    wavesNum: ov.wavesNum ?? base.wavesNum,
    monstersPerWave: ov.monstersPerWave ?? base.monstersPerWave,
    monsterBaseHp: ov.monsterBaseHp ?? base.monsterBaseHp,
  };
}

/** Serialize an override to JSON string for storage. */
export function serializeOverride(o: FieldOverride): string {
  return JSON.stringify(o);
}

/** Parse a JSON string back to FieldOverride, returning null on invalid input. */
export function parseOverride(s: string): FieldOverride | null {
  try { return JSON.parse(s) as FieldOverride; } catch { return null; }
}

// ---------------------------------------------------------------------------
// Back-compat aliases — remove once all callers migrated
// ---------------------------------------------------------------------------
/** @deprecated Use applyOverride */
export const applyFieldOverride = applyOverride;
